import { Check, ChevronUp, Gauge, LogOut, Settings } from 'lucide-react'
import { Link } from 'react-router-dom'

import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTeam } from '@/contexts/TeamContext'
import { useAuth } from '@/contexts/AuthContext'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { isNavRouteVisible } from '@/lib/nav'

/**
 * `collapsed`: the folded sidebar rail (TRA-1085) shows the avatar only.
 * The avatar holds its place and the trigger its height in both states
 * (px-0.5 centers it on the sidebar's icon column, min-h-10 fits the
 * two-line label), so folding moves nothing.
 */
export function TeamMenu({ collapsed = false }: { collapsed?: boolean }): React.JSX.Element {
  const { activeTeam, vanityOrg } = useTeam()
  const { email, logout } = useAuth()
  const teamName = activeTeam?.name ?? vanityOrg ?? 'Team'
  return (
    <DropdownMenu>
      <TeamMenuTrigger teamName={teamName} email={email} collapsed={collapsed} />
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel className="break-words font-normal">
          {teamName}
          <span className="block break-all text-xs text-muted-foreground">{email}</span>
        </DropdownMenuLabel>
        <TeamMenuLinks />
        <TeamSwitchItems />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout}><LogOut />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface TeamMenuTriggerProps {
  teamName: string
  email: string | null
  collapsed: boolean
}

function TeamMenuTrigger({ teamName, email, collapsed }: TeamMenuTriggerProps): React.JSX.Element {
  return (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        aria-label="Team and account menu"
        title={collapsed ? `${teamName} (${email ?? 'Unknown'})` : undefined}
        className="flex min-h-10 w-full items-center gap-2 rounded-md px-0.5 py-1 text-foreground/80 hover:bg-foreground/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
      >
        <Avatar className="h-7 w-7 shrink-0">
          <AvatarFallback className="bg-muted text-xs">{email?.charAt(0).toUpperCase() || '?'}</AvatarFallback>
        </Avatar>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 text-left">
              <span className="block truncate text-xs">{teamName}</span>
              <span className="block truncate text-xs text-muted-foreground">{email ?? 'Unknown'}</span>
            </span>
            <ChevronUp className="mr-1.5 h-3 w-3 shrink-0" />
          </>
        )}
      </button>
    </DropdownMenuTrigger>
  )
}

function TeamMenuLinks(): React.JSX.Element | null {
  const { isRestricted, hiddenNavItems, meLoaded, navConfigLoaded } = useTeam()
  if (!meLoaded || isRestricted) return null
  const canSeeUsage = isNavRouteVisible({ to: '/', hiddenNavItems, navConfigLoaded })
  return (
    <>
      <DropdownMenuSeparator />
      {canSeeUsage && <DropdownMenuItem asChild><Link to="/"><Gauge />Usage</Link></DropdownMenuItem>}
      <DropdownMenuItem asChild><Link to="/settings"><Settings />Settings</Link></DropdownMenuItem>
    </>
  )
}

function TeamSwitchItems(): React.JSX.Element | null {
  const { activeTeam, teams, switchTeam, vanityOrg } = useTeam()
  if (teams.length < 2 || vanityOrg !== null) return null
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="font-normal text-muted-foreground">Switch team</DropdownMenuLabel>
      {teams.map((team) => (
        <DropdownMenuItem key={team.id} onClick={() => { switchTeam(team.id) }}>
          {team.id === activeTeam?.id ? <Check className="h-3.5 w-3.5" /> : <span className="w-3.5" />}
          {team.name}
        </DropdownMenuItem>
      ))}
    </>
  )
}
